import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { motion } from 'framer-motion';
import { Mail, Lock, User, Phone, ArrowLeft, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';

const LOGO = "/logo.jpeg";
const HERO_BG = "https://static.prod-images.emergentagent.com/jobs/d38cceef-fa87-41ff-8976-865f3a1e1cf6/images/c81804714d610e7e566d328a38e3a5253dd3e1c617891ef028e2a2daef72f95f.png";

export default function AuthPage() {
  const [mode, setMode] = useState('login');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', password: '', phone: '' });
  const { login, register, googleLogin } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === 'login') {
        await login(form.email, form.password);
        toast.success('Welcome back!');
      } else {
        if (!form.name.trim()) { toast.error('Name is required'); setLoading(false); return; }
        await register(form.name, form.email, form.password, form.phone);
        toast.success('Account created!');
      }
      const target = sessionStorage.getItem('post_login_redirect');
      if (target) { sessionStorage.removeItem('post_login_redirect'); navigate(target, { replace: true }); }
      else navigate('/dashboard', { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Authentication failed');
    } finally {
      setLoading(false);
    }
  };

  const inputClasses = "w-full bg-transparent border-b border-white/15 text-white py-3.5 pl-10 pr-4 focus:border-[#EF4444] focus:outline-none transition-all placeholder:text-[#505068] text-[14px] tracking-wide";

  return (
    <div className="min-h-screen flex" data-testid="auth-page">
      {/* Left - Image (desktop only) */}
      <div className="hidden lg:block lg:w-1/2 relative">
        <img loading="lazy" decoding="async" src={HERO_BG} alt="" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-transparent to-[#0A0A14]" />
        <div className="absolute bottom-12 left-12 right-12">
          <h2 className="font-heading text-4xl font-light text-[#FCA5A5] mb-4">Unlock Your Musical Potential</h2>
          <p className="text-[#B0B0BE] font-light">Join Musicoul and begin your journey with master musicians.</p>
        </div>
      </div>

      {/* Right - Form */}
      <div className="flex-1 flex flex-col items-center justify-center px-4 sm:px-6 py-8 sm:py-12 bg-[#0A0A14] min-h-screen">
        <div className="w-full max-w-sm">
          <Link to="/" className="inline-flex items-center gap-2 text-[#B0B0BE] text-sm mb-6 sm:mb-10 hover:text-[#F59E0B] transition-colors" data-testid="back-to-home">
            <ArrowLeft size={16} /> Back to Home
          </Link>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <img loading="lazy" decoding="async" src={LOGO} alt="Musicoul" className="w-14 h-14 rounded-full mb-8" />
            <h1 className="font-heading text-2xl tracking-wide text-[#FCA5A5] mb-2">
              {mode === 'login' ? 'Welcome Back' : 'Join Musicoul'}
            </h1>
            <p className="text-[13px] text-[#6B6B80] mb-10">
              {mode === 'login' ? 'Sign in to continue your musical journey' : 'Create an account to begin learning'}
            </p>

            <form onSubmit={handleSubmit} className="space-y-5" data-testid="auth-form">
              {mode === 'register' && (
                <>
                  <div className="relative">
                    <User size={16} className="absolute left-0 top-1/2 -translate-y-1/2 text-[#666]" />
                    <input type="text" placeholder="Full Name" value={form.name} onChange={e => setForm({...form, name: e.target.value})} className={inputClasses} data-testid="input-name" required />
                  </div>
                  <div className="relative">
                    <Phone size={16} className="absolute left-0 top-1/2 -translate-y-1/2 text-[#666]" />
                    <input type="tel" placeholder="Phone Number" value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} className={inputClasses} data-testid="input-phone" />
                  </div>
                </>
              )}
              <div className="relative">
                <Mail size={16} className="absolute left-0 top-1/2 -translate-y-1/2 text-[#666]" />
                <input type="email" placeholder="Email Address" value={form.email} onChange={e => setForm({...form, email: e.target.value})} className={inputClasses} data-testid="input-email" required />
              </div>
              <div className="relative">
                <Lock size={16} className="absolute left-0 top-1/2 -translate-y-1/2 text-[#666]" />
                <input type={showPassword ? 'text' : 'password'} placeholder="Password" value={form.password} onChange={e => setForm({...form, password: e.target.value})} className={inputClasses} data-testid="input-password" required />
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-0 top-1/2 -translate-y-1/2 text-[#666] hover:text-[#F59E0B]">
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>

              <button type="submit" disabled={loading} className="w-full bg-gradient-to-r from-[#EF4444] to-[#8B5CF6] text-white py-3 font-medium hover:shadow-[0_0_20px_rgba(139,92,246,0.3)] transition-colors disabled:opacity-50 flex items-center justify-center gap-2" data-testid="auth-submit-btn">
                {loading ? <div className="w-5 h-5 border-2 border-black border-t-transparent rounded-full animate-spin" /> : (mode === 'login' ? 'Sign In' : 'Create Account')}
              </button>
            </form>

            <div className="relative my-8">
              <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-white/10" /></div>
              <div className="relative flex justify-center"><span className="bg-[#0A0A14] px-4 text-xs text-[#666]">or continue with</span></div>
            </div>

            <button onClick={googleLogin} className="w-full border border-white/10 py-3 text-white hover:bg-white/5 transition-colors flex items-center justify-center gap-3 text-sm" data-testid="google-login-btn">
              <svg viewBox="0 0 24 24" width="18" height="18"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
              Sign in with Google
            </button>

            <p className="text-center text-sm text-[#666] mt-8">
              {mode === 'login' ? "Don't have an account? " : "Already have an account? "}
              <button onClick={() => setMode(mode === 'login' ? 'register' : 'login')} className="text-[#F59E0B] hover:text-[#FCA5A5] transition-colors" data-testid="toggle-auth-mode">
                {mode === 'login' ? 'Register' : 'Sign In'}
              </button>
            </p>
          </motion.div>
        </div>
      </div>
    </div>
  );
}
